import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { formatAddress } from '../lib/blockchain';

const getWalletAddress = (props) =>
  props?.account ||
  props?.wallet?.account ||
  props?.wallet?.address ||
  props?.walletState?.account ||
  props?.walletState?.address ||
  props?.metaMask?.account ||
  props?.metaMask?.address ||
  '';

const DEFAULT_FORM = {
  strategy: 'staking-rewards',
  amount: '1000',
  apyBps: '850',
  cadenceDays: '7',
  horizonDays: '90',
};

const DEFAULT_OPPORTUNITY_FILTERS = {
  chain: '',
  project: '',
  symbol: '',
  stablecoin: '',
  compoundOnly: true,
};

const formatValue = (value, fallback = '—') => {
  if (value === undefined || value === null || value === '') return fallback;
  return `${value}`;
};

const formatAmount = (value, suffix = '') => {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return '—';
  return `${parsed.toLocaleString(undefined, { maximumFractionDigits: 6 })}${suffix}`;
};

const formatBps = (value) => {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return '—';
  return `${(parsed / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
};

const formatUsd = (value) => {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return '—';
  return `$${parsed.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const formatApyPercent = (value) => {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return '—';
  return `${parsed.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
};

function MetricCard({ label, value, helper }) {
  return (
    <article className="glass p-5">
      <p className="text-sm text-slate-400">{label}</p>
      <h2 className="mt-2 text-2xl font-semibold text-white">{value}</h2>
      {helper ? <p className="mt-1 text-sm text-slate-400">{helper}</p> : null}
    </article>
  );
}

export default function Compound(props) {
  const account = getWalletAddress(props);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [quote, setQuote] = useState(null);
  const [intent, setIntent] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [loadingOpportunities, setLoadingOpportunities] = useState(false);
  const [loadingConnection, setLoadingConnection] = useState(false);
  const [opportunityFilters, setOpportunityFilters] = useState(DEFAULT_OPPORTUNITY_FILTERS);
  const [opportunities, setOpportunities] = useState([]);
  const [opportunityMeta, setOpportunityMeta] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [readOnlyConnection, setReadOnlyConnection] = useState(null);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  useEffect(() => {
    const loadStatus = async () => {
      setLoadingStatus(true);
      try {
        const response = await api.defi.compound.status();
        setStatus(response);
        const defaultStrategy = response?.strategies?.[0];

        if (defaultStrategy) {
          setForm((current) => ({
            ...current,
            strategy: defaultStrategy.id || current.strategy,
            apyBps: `${defaultStrategy.defaultApyBps || current.apyBps}`,
            cadenceDays: `${defaultStrategy.defaultCadenceDays || current.cadenceDays}`,
          }));
        }
      } catch (err) {
        setFeedback({
          type: 'error',
          message: err?.response?.data?.error || err?.message || 'Unable to load compound status.',
        });
      } finally {
        setLoadingStatus(false);
      }
    };

    const loadInitialOpportunities = async () => {
      setLoadingOpportunities(true);
      try {
        const response = await api.defi.compound.opportunities({
          compound_only: 'true',
          limit: 24,
        });
        setOpportunities(response?.opportunities || []);
        setOpportunityMeta(response || null);
      } catch (err) {
        setOpportunityMeta({
          success: false,
          error: err?.response?.data?.error || err?.message || 'Unable to load external opportunities.',
        });
      } finally {
        setLoadingOpportunities(false);
      }
    };

    loadStatus();
    loadInitialOpportunities();
  }, []);

  const strategies = useMemo(() => status?.strategies || [], [status]);
  const selectedStrategy = useMemo(
    () => strategies.find((strategy) => strategy.id === form.strategy) || strategies[0],
    [form.strategy, strategies],
  );
  const statusMetrics = useMemo(
    () => [
      {
        label: 'Mode',
        value: loadingStatus ? 'Loading…' : formatValue(status?.mode, 'sandbox'),
        helper: 'Backend guardrail',
      },
      {
        label: 'Intents',
        value: status?.intent_creation_enabled ? 'Enabled' : 'Read-only',
        helper: 'Disabled until feature flag activation',
      },
      {
        label: 'Max amount',
        value: formatAmount(status?.limits?.max_amount, ` ${selectedStrategy?.asset || 'ITANI'}`),
        helper: 'Sandbox cap',
      },
      {
        label: 'Max APY',
        value: formatBps(status?.limits?.max_apy_bps),
        helper: 'Projection cap',
      },
    ],
    [loadingStatus, selectedStrategy?.asset, status],
  );
  const quoteMetrics = useMemo(
    () => [
      {
        label: 'Projected balance',
        value: formatAmount(quote?.projected_balance, ` ${quote?.asset || selectedStrategy?.asset || ''}`),
        helper: `${formatValue(quote?.horizon_days, form.horizonDays)} days`,
      },
      {
        label: 'Projected rewards',
        value: formatAmount(quote?.projected_rewards, ` ${quote?.asset || selectedStrategy?.asset || ''}`),
        helper: `${formatBps(quote?.apy_bps || form.apyBps)} APY`,
      },
      {
        label: 'Compound bonus',
        value: formatAmount(quote?.compound_bonus, ` ${quote?.asset || selectedStrategy?.asset || ''}`),
        helper: 'Against simple interest',
      },
      {
        label: 'Cycles',
        value: formatValue(quote?.cycles, '—'),
        helper: `${formatValue(quote?.cadence_days, form.cadenceDays)} day cadence`,
      },
    ],
    [form.apyBps, form.cadenceDays, form.horizonDays, quote, selectedStrategy?.asset],
  );

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setIntent(null);
  };

  const updateOpportunityFilter = (field, value) => {
    setOpportunityFilters((current) => ({ ...current, [field]: value }));
  };

  const handleStrategyChange = (event) => {
    const nextStrategy = strategies.find((strategy) => strategy.id === event.target.value);
    setForm((current) => ({
      ...current,
      strategy: event.target.value,
      apyBps: `${nextStrategy?.defaultApyBps || current.apyBps}`,
      cadenceDays: `${nextStrategy?.defaultCadenceDays || current.cadenceDays}`,
    }));
    setIntent(null);
  };

  const buildPayload = () => ({
    strategy: form.strategy,
    amount: form.amount,
    apy_bps: Number(form.apyBps),
    cadence_days: Number(form.cadenceDays),
    horizon_days: Number(form.horizonDays),
  });

  const loadOpportunities = async (event) => {
    if (event) event.preventDefault();
    setLoadingOpportunities(true);
    setOpportunityMeta(null);

    try {
      const response = await api.defi.compound.opportunities({
        chain: opportunityFilters.chain || undefined,
        project: opportunityFilters.project || undefined,
        symbol: opportunityFilters.symbol || undefined,
        stablecoin: opportunityFilters.stablecoin || undefined,
        compound_only: opportunityFilters.compoundOnly ? 'true' : 'false',
        limit: 24,
      });
      setOpportunities(response?.opportunities || []);
      setOpportunityMeta(response || null);
    } catch (err) {
      setOpportunities([]);
      setOpportunityMeta({
        success: false,
        error: err?.response?.data?.error || err?.message || 'Unable to load external opportunities.',
      });
    } finally {
      setLoadingOpportunities(false);
    }
  };

  const connectOpportunity = async (opportunity) => {
    setLoadingConnection(true);
    setFeedback({ type: '', message: '' });

    try {
      const response = await api.defi.compound.connect({
        opportunity,
        wallet_address: account || undefined,
        amount: form.amount,
        cadence_days: Number(form.cadenceDays),
        horizon_days: Number(form.horizonDays),
      });
      setReadOnlyConnection(response);
      setSelectedOpportunity(response?.opportunity || opportunity);
      setQuote(response?.projection || null);
      setForm((current) => ({
        ...current,
        strategy: 'external-yield',
        apyBps: `${response?.opportunity?.projection_apy_bps || opportunity.projection_apy_bps || current.apyBps}`,
      }));
      setIntent(null);
      setFeedback({
        type: 'success',
        message: `${response?.connector?.label || opportunity.project} connected in read-only mode.`,
      });
    } catch (err) {
      setReadOnlyConnection(null);
      setFeedback({
        type: 'error',
        message: err?.response?.data?.error || err?.message || 'Unable to connect this opportunity.',
      });
    } finally {
      setLoadingConnection(false);
    }
  };

  const loadQuote = async (event) => {
    if (event) event.preventDefault();
    setLoadingQuote(true);
    setFeedback({ type: '', message: '' });
    setIntent(null);

    try {
      const response = await api.defi.compound.quote(buildPayload());
      setQuote(response?.quote || response);
    } catch (err) {
      setQuote(null);
      setFeedback({
        type: 'error',
        message: err?.response?.data?.error || err?.message || 'Unable to build compound projection.',
      });
    } finally {
      setLoadingQuote(false);
    }
  };

  const createIntent = async () => {
    setLoadingIntent(true);
    setFeedback({ type: '', message: '' });

    if (!account) {
      setLoadingIntent(false);
      setFeedback({ type: 'error', message: 'Connect a wallet before creating a sandbox intent.' });
      return;
    }

    try {
      const response = await api.defi.compound.createIntent({
        ...buildPayload(),
        wallet_address: account,
      });
      setIntent(response?.intent || response);
      setQuote(response?.intent?.projection || quote);
      setFeedback({ type: 'success', message: 'Sandbox compound intent created.' });
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err?.response?.data?.error || err?.message || 'Unable to create compound intent.',
      });
    } finally {
      setLoadingIntent(false);
    }
  };

  const canCreateIntent = Boolean(status?.intent_creation_enabled && account && quote && !loadingIntent);

  return (
    <div className="space-y-6">
      <section className="glass p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm uppercase tracking-wide text-slate-400">DeFi Compound</p>
            <h1 className="mt-2 text-3xl font-semibold text-white">Auto-compound sandbox planner</h1>
            <p className="mt-2 text-slate-300">
              Build a projected reinvestment plan for staking or liquidity rewards without submitting on-chain actions.
            </p>
          </div>

          <div className="glass rounded-2xl px-4 py-3 text-right">
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Wallet</p>
            <p className="text-sm font-semibold text-cyan-300">{account ? formatAddress(account) : 'Not connected'}</p>
          </div>
        </div>
      </section>

      {feedback.message ? (
        <section
          className={`glass p-4 ${
            feedback.type === 'error'
              ? 'border border-rose-500/30 text-rose-200'
              : 'border border-emerald-500/30 text-emerald-200'
          }`}
        >
          {feedback.message}
        </section>
      ) : null}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {statusMetrics.map((metric) => (
          <MetricCard key={metric.label} {...metric} />
        ))}
      </section>

      <section className="glass p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm uppercase tracking-wide text-slate-400">Multi-chain radar</p>
            <h2 className="mt-1 text-2xl font-semibold text-white">External compound opportunities</h2>
            <p className="mt-2 text-sm text-slate-300">
              Read-only yield data for projection inputs. HudLife does not execute deposits, approvals, swaps, or claims.
            </p>
          </div>
          <div className="text-sm text-slate-400">
            {opportunityMeta?.cached ? 'Cached source' : opportunityMeta?.source || 'External yields'}
          </div>
        </div>

        <form className="mt-5 grid gap-3 lg:grid-cols-[1fr_1fr_1fr_0.8fr_auto]" onSubmit={loadOpportunities}>
          <input
            className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none"
            placeholder="Chain"
            value={opportunityFilters.chain}
            onChange={(event) => updateOpportunityFilter('chain', event.target.value)}
          />
          <input
            className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none"
            placeholder="Project"
            value={opportunityFilters.project}
            onChange={(event) => updateOpportunityFilter('project', event.target.value)}
          />
          <input
            className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none"
            placeholder="Symbol"
            value={opportunityFilters.symbol}
            onChange={(event) => updateOpportunityFilter('symbol', event.target.value)}
          />
          <select
            className="rounded-2xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-white outline-none"
            value={opportunityFilters.stablecoin}
            onChange={(event) => updateOpportunityFilter('stablecoin', event.target.value)}
          >
            <option value="">Any asset</option>
            <option value="true">Stable only</option>
            <option value="false">Volatile assets</option>
          </select>
          <button
            type="submit"
            className="rounded-full bg-cyan-400 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={loadingOpportunities}
          >
            {loadingOpportunities ? 'Loading...' : 'Refresh'}
          </button>
        </form>

        <label className="mt-3 flex items-center gap-3 text-sm text-slate-300">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-white/20 bg-slate-950"
            checked={opportunityFilters.compoundOnly}
            onChange={(event) => updateOpportunityFilter('compoundOnly', event.target.checked)}
          />
          Compound, lending, vault, staking, or auto-compound signals only
        </label>

        {opportunityMeta?.error ? (
          <div className="mt-4 rounded-2xl border border-amber-400/20 bg-amber-400/10 p-4 text-sm text-amber-100">
            {opportunityMeta.error}
          </div>
        ) : null}

        {readOnlyConnection ? (
          <div className="mt-4 grid gap-4 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 p-4 text-sm text-cyan-100 lg:grid-cols-[1fr_auto]">
            <div>
              <p className="font-semibold">
                {readOnlyConnection.connector?.label || 'External protocol'} connected read-only
              </p>
              <p className="mt-1 text-cyan-100/80">
                {selectedOpportunity?.project} / {selectedOpportunity?.symbol} on {selectedOpportunity?.chain}
              </p>
              <p className="mt-2 text-xs text-cyan-100/70">
                Blocked: transaction, ERC-20 approval, bridge, custody.
              </p>
            </div>
            {readOnlyConnection.connector?.primary_url ? (
              <a
                className="inline-flex items-center justify-center rounded-full border border-cyan-200/30 px-4 py-2 text-xs font-semibold text-cyan-50 transition hover:bg-cyan-200/10"
                href={readOnlyConnection.connector.primary_url}
                target="_blank"
                rel="noreferrer"
              >
                Open protocol
              </a>
            ) : null}
          </div>
        ) : selectedOpportunity ? (
          <div className="mt-4 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 p-4 text-sm text-cyan-100">
            Projection input: {selectedOpportunity.project} / {selectedOpportunity.symbol} on {selectedOpportunity.chain}
          </div>
        ) : null}

        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-slate-400">
              <tr>
                <th className="pb-3 pr-4 font-medium">Chain</th>
                <th className="pb-3 pr-4 font-medium">Project</th>
                <th className="pb-3 pr-4 font-medium">Pool</th>
                <th className="pb-3 pr-4 font-medium">APY</th>
                <th className="pb-3 pr-4 font-medium">TVL</th>
                <th className="pb-3 pr-4 font-medium">Signal</th>
                <th className="pb-3 font-medium">Connection</th>
              </tr>
            </thead>
            <tbody>
              {opportunities.map((opportunity) => (
                <tr key={opportunity.pool_id} className="border-t border-white/5">
                  <td className="py-3 pr-4 text-white">{opportunity.chain}</td>
                  <td className="py-3 pr-4 text-slate-300">{opportunity.project}</td>
                  <td className="max-w-[240px] truncate py-3 pr-4 text-slate-300">
                    {opportunity.symbol}
                    {opportunity.pool_meta ? (
                      <span className="ml-2 text-xs text-slate-500">{opportunity.pool_meta}</span>
                    ) : null}
                  </td>
                  <td className="py-3 pr-4 text-cyan-200">{formatApyPercent(opportunity.apy)}</td>
                  <td className="py-3 pr-4 text-slate-300">{formatUsd(opportunity.tvl_usd)}</td>
                  <td className="py-3 pr-4">
                    <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300">
                      {opportunity.compound_signal}
                    </span>
                  </td>
                  <td className="py-3">
                    <button
                      type="button"
                      className="rounded-full border border-white/10 px-3 py-1 text-xs text-white transition hover:bg-white/5"
                      disabled={loadingConnection}
                      onClick={() => connectOpportunity(opportunity)}
                    >
                      Connect read-only
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loadingOpportunities && opportunities.length === 0 ? (
          <p className="mt-4 text-sm text-slate-300">No external opportunities match the current filters.</p>
        ) : null}
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <article className="glass p-6">
          <h2 className="text-xl font-semibold text-white">Projection</h2>
          <form className="mt-4 space-y-4" onSubmit={loadQuote}>
            <label className="block">
              <span className="mb-2 block text-sm text-slate-400">Strategy</span>
              <select
                className="w-full rounded-2xl border border-white/10 bg-slate-950 px-4 py-3 text-white outline-none"
                value={form.strategy}
                onChange={handleStrategyChange}
              >
                {strategies.length ? (
                  strategies.map((strategy) => (
                    <option key={strategy.id} value={strategy.id}>
                      {strategy.label}
                    </option>
                  ))
                ) : (
                  <option value={form.strategy}>Sandbox strategy</option>
                )}
              </select>
            </label>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm text-slate-400">Amount</span>
                <input
                  type="number"
                  min="0"
                  step="any"
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none"
                  value={form.amount}
                  onChange={(event) => updateForm('amount', event.target.value)}
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-sm text-slate-400">APY basis points</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none"
                  value={form.apyBps}
                  onChange={(event) => updateForm('apyBps', event.target.value)}
                />
              </label>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm text-slate-400">Cadence days</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none"
                  value={form.cadenceDays}
                  onChange={(event) => updateForm('cadenceDays', event.target.value)}
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-sm text-slate-400">Horizon days</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none"
                  value={form.horizonDays}
                  onChange={(event) => updateForm('horizonDays', event.target.value)}
                />
              </label>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                className="rounded-full bg-cyan-400 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={loadingQuote}
              >
                {loadingQuote ? 'Calculating…' : 'Preview compound'}
              </button>
              <button
                type="button"
                className="rounded-full border border-white/10 px-4 py-2 text-sm text-white transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!canCreateIntent}
                onClick={createIntent}
              >
                {loadingIntent ? 'Creating…' : 'Create sandbox intent'}
              </button>
            </div>
          </form>
        </article>

        <article className="glass p-6">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-semibold text-white">Projected outcome</h2>
            <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300">
              {quote?.strategy_label || selectedStrategy?.label || 'No quote'}
            </span>
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {quoteMetrics.map((metric) => (
              <div key={metric.label} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <p className="text-sm text-slate-400">{metric.label}</p>
                <p className="mt-2 text-xl font-semibold text-white">{metric.value}</p>
                <p className="mt-1 text-xs text-slate-500">{metric.helper}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-2xl border border-amber-400/20 bg-amber-400/10 p-4 text-sm text-amber-100">
            Simulation only. No transaction is signed or submitted by this module.
          </div>
        </article>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <article className="glass p-6">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-semibold text-white">Schedule preview</h2>
            <span className="text-sm text-slate-400">{quote?.schedule_preview?.length || 0} rows</span>
          </div>

          {quote?.schedule_preview?.length ? (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-slate-400">
                  <tr>
                    <th className="pb-3 pr-4 font-medium">Cycle</th>
                    <th className="pb-3 pr-4 font-medium">Day</th>
                    <th className="pb-3 pr-4 font-medium">Reward</th>
                    <th className="pb-3 font-medium">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.schedule_preview.map((row) => (
                    <tr key={`${row.cycle}-${row.day}`} className="border-t border-white/5">
                      <td className="py-3 pr-4 text-white">{row.cycle}</td>
                      <td className="py-3 pr-4 text-slate-300">{row.day}</td>
                      <td className="py-3 pr-4 text-slate-300">{formatAmount(row.reward)}</td>
                      <td className="py-3 text-slate-300">{formatAmount(row.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-4 text-slate-300">Run a projection to view the compounding schedule.</p>
          )}
        </article>

        <article className="glass p-6">
          <h2 className="text-xl font-semibold text-white">Guardrails</h2>
          <div className="mt-4 space-y-3">
            {(status?.guardrails || []).map((guardrail) => (
              <div key={guardrail} className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-300">
                {guardrail}
              </div>
            ))}
          </div>

          {intent ? (
            <div className="mt-5 rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-4 text-sm text-emerald-100">
              <p className="font-semibold">Intent {intent.intent_id}</p>
              <p className="mt-1">
                Status: {intent.status}. Transaction submitted: {intent.transaction_submitted ? 'yes' : 'no'}.
              </p>
            </div>
          ) : null}
        </article>
      </section>
    </div>
  );
}
